import {
  FlowStep,
  FundingOption,
  UpsertFlowStepInput,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { useSavingsRateInfo } from 'features/accounts/hooks/useSavingsRateInfo';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import disclosureUrl from 'features/onboarding/constants/DisclosureUrl';
import useFundingNavigation from 'features/onboarding/hooks/useFundingNavigation';
import useGetFlowStepId from 'features/onboarding/hooks/useGetFlowStepId';
import useUpsertFlowStep from 'features/onboarding/hooks/useUpsertFlowStep';
import { redirectUserToTargetRoute } from 'features/onboarding/utils/redirectUserToTargetRoute';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const GetPaidEarlyScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('onboarding');

  const { navigateFundingAction, pinWheelLoading: isPinwheelLoading } =
    useFundingNavigation({ deferDdFlowInit: true });

  const flowStepId = useGetFlowStepId();

  const { isUpsertFlowStepLoading, submitUpsertFlowStep } = useUpsertFlowStep();

  const {
    rates: savingsRates,
    fetching: areSavingsRatesFetching,
    isUnavailable: areSavingsRatesUnavailable,
    refetch: refetchSavingsRates,
  } = useSavingsRateInfo();
  const { defaultTitle, defaultMessage, retryButtonTitle } =
    useDefaultErrorStrings();

  const handleContinueButton = async (didSkip?: boolean) => {
    const isSkipped = Boolean(didSkip);
    const input: UpsertFlowStepInput = {
      id: flowStepId,
      intentBasedOnboarding: {
        selection: undefined,
        didSkip: isSkipped,
      },
    };

    const submitHandler = async () => {
      const result = await submitUpsertFlowStep({ input });
      if (!result.error) {
        redirectUserToTargetRoute(result.data?.upsertFlowStep as FlowStep);
      }
    };

    try {
      // Only navigate if the mutation was successful and not cancelled
      if (isSkipped) {
        await submitHandler();
      } else {
        navigateFundingAction(FundingOption.DirectDeposit, submitHandler);
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
    }
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: 'get-paid-early',
  });

  return (
    <View style={styles.container}>
      <NavigationBar
        title=""
        trailingAccessories={
          <Button
            label={translatedText('shared.skip')}
            size="SMALL"
            type="TEXT"
            width="HUG"
            onPress={() => handleContinueButton(true)}
            analyticsId="skipEarlyPayDay"
          />
        }
      />

      {savingsRates ? (
        <ActionScreen
          primaryButton={{
            label: translatedText('getPaidEarly.button'),
            onPress: handleContinueButton,
            isLoading: isPinwheelLoading,
            analyticsId: 'directDepositClicked',
          }}
          bodyContent={
            <>
              <BodyBaseText style={styles.paddingHorizontal}>
                {translatedText('getPaidEarly.title')}
              </BodyBaseText>
              <ListItem
                primaryCopy={translatedText('getPaidEarly.listItem1', {
                  ...savingsRates,
                })}
                primaryNumberOfLines={0}
                leadingAccessory={{
                  id: 'piggybankIcon',
                  name: VXLComponentId.ICON,
                  props: {
                    iconKey: 'PIGGY_BANK',
                  },
                }}
                showsChevron={false}
              />
              <View style={styles.hairLineDivider}>
                <HairLineDivider />
              </View>
              <ListItem
                primaryCopy={translatedText('getPaidEarly.listItem2')}
                primaryNumberOfLines={0}
                leadingAccessory={{
                  id: 'ddIcon',
                  name: VXLComponentId.ICON,
                  props: {
                    iconKey: 'DIRECT_DEPOSIT',
                  },
                }}
                showsChevron={false}
              />
              <View style={styles.hairLineDivider}>
                <HairLineDivider />
              </View>
              <ListItem
                primaryCopy={translatedText('getPaidEarly.listItem3')}
                primaryNumberOfLines={0}
                leadingAccessory={{
                  id: 'clockIcon',
                  name: VXLComponentId.ICON,
                  props: {
                    iconKey: 'CLOCK',
                  },
                }}
                showsChevron={false}
              />
              <View style={styles.wrapperWithPadding}>
                <HyperlinkSmText
                  onPress={() => {
                    router.push({
                      pathname: '/onboarding/disclosure',
                      params: {
                        pdfUrl: disclosureUrl,
                      },
                    });
                  }}
                  analyticsId="disclosureClicked"
                >
                  {translatedText('getPaidEarly.disclosure')}
                </HyperlinkSmText>
              </View>
            </>
          }
          visual="CALENDAR_A"
          titleText={translatedText('getPaidEarly.headlineText')}
          titleTextStyle="extra-large"
          includeDefaultPadding={false}
        />
      ) : null}

      <ScreenTransitionOverlay
        isLoading={areSavingsRatesFetching && !savingsRates}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          areSavingsRatesUnavailable ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultTitle}
              messageBody={defaultMessage}
              primaryButton={{
                title: retryButtonTitle,
                onPress: refetchSavingsRates,
              }}
            />
          ) : null
        }
      />

      <ScreenTransitionOverlay
        isLoading={isUpsertFlowStepLoading || isPinwheelLoading}
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={isUpsertFlowStepLoading}
            isPinwheelLoading={isPinwheelLoading}
            defaultLoadingComponent={
              <FullScreenLoading
                isCoinLoader
                titleText={translatedText('loading.titleText')}
              />
            }
          />
        }
        ErrorComponent={null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  hairLineDivider: {
    paddingHorizontal: theme.spacing.m,
  },
  paddingHorizontal: { paddingHorizontal: theme.spacing.m },
  wrapperWithPadding: { padding: theme.spacing.m },
});

export default GetPaidEarlyScreen;
